import {
  IconBriefcase,
  IconBuilding,
  IconCalendar,
  IconCash,
  IconClipboard,
  IconCreditCard,
  IconFile,
  IconFileText,
  IconMail,
  IconMapPin,
  IconMinus,
  IconPhone,
  IconPlus,
  IconShieldLock,
  IconUser,
  IconWallet,
} from "@tabler/icons-react";
import { COLORS, MAX_ITEMS_PER_PAGE } from "./constants/constants";
import type { ContentItem, DocumentItem, FieldObject } from "./types";
import { useProfileStore } from "@pages/ClientProfile/store/profileStore.ts";
import { showNotification } from "@mantine/notifications";
import { canAddItems } from "./constants/dynamicFieldsConfig";

/**
 * Returns an icon based on the given label text.
 */
export const getIconForLabel = (text: string): JSX.Element | null => {
  // Early return for empty or null text
  if (!text || typeof text !== "string") return null;

  const lowerText = text.toLowerCase().trim();

  // Use a more efficient lookup pattern
  const iconMap: Record<string, string> = {
    name: "user",
    address: "map-pin",
    phone: "phone",
    email: "mail",
    ssn: "shield-lock",
    "date of birth": "calendar",
    income: "dollar-sign",
    wages: "briefcase",
    business: "building",
    deductions: "minus-circle",
    credits: "plus-circle",
    tax: "file-text",
    document: "file",
    form: "clipboard",
    bank: "credit-card",
    account: "wallet",
  };

  // Check for exact matches first
  for (const [key, icon] of Object.entries(iconMap)) {
    if (lowerText.includes(key)) {
      return getIconElement(icon);
    }
  }

  return null;
};

/**
 * Helper function to get icon element
 */
function getIconElement(iconName: string): JSX.Element | null {
  const iconMap: Record<string, JSX.Element> = {
    user: <IconUser size={16} />,
    "map-pin": <IconMapPin size={16} />,
    phone: <IconPhone size={16} />,
    mail: <IconMail size={16} />,
    "shield-lock": <IconShieldLock size={16} />,
    calendar: <IconCalendar size={16} />,
    "dollar-sign": <IconCash size={16} />,
    briefcase: <IconBriefcase size={16} />,
    building: <IconBuilding size={16} />,
    "minus-circle": <IconMinus size={16} />,
    "plus-circle": <IconPlus size={16} />,
    "file-text": <IconFileText size={16} />,
    file: <IconFile size={16} />,
    clipboard: <IconClipboard size={16} />,
    "credit-card": <IconCreditCard size={16} />,
    wallet: <IconWallet size={16} />,
  };

  return iconMap[iconName] || null;
}

export function getLabelStyle(level: "top" | "sub" | "subsub"): {
  backgroundColor: string;
  fontWeight: number;
} {
  switch (level) {
    case "top":
      return { backgroundColor: COLORS.topLevel, fontWeight: 600 };
    case "sub":
      return { backgroundColor: COLORS.subLevel, fontWeight: 400 };
    case "subsub":
      return { backgroundColor: COLORS.subSubLevel, fontWeight: 500 };
    default:
      return { backgroundColor: COLORS.subLevel, fontWeight: 400 };
  }
}

/**
 * Recursively flattens a nested content structure into a linear array of content items.
 * Simplified version without caching - useMemo handles memoization at the component level.
 *
 * @param node - The input node to flatten, which can contain labels, fields, and nested child nodes
 * @param indentLevel - The current indentation level for hierarchical representation, defaults to 0
 * @returns An array of flattened ContentItem objects representing the node's structure
 */
export function flattenContent(
  node: {
    label?: string;
    key?: string;
    fields?: Record<string, FieldObject>;
  } & Record<string, unknown>,
  indentLevel = 0,
  formType?: string | null
): ContentItem[] {
  if (!node || typeof node !== "object") return [];

  // If the node is a document node, create a DocumentItem
  const isDocumentNode = checkIfDocumentNode(node);

  if (isDocumentNode) {
    return [createDocumentItem(node, indentLevel)];
  }

  const childItems = processChildNodes(node, indentLevel, formType);

  let fieldItems: ContentItem[] = [];

  if (node.fields && typeof node.fields === "object") {
    fieldItems = processFields(node.fields, node.key, indentLevel, formType);
  }

  // Check if this node itself has an 'items' array that might be a dynamic field
  // This handles cases like 1095A where coverage_information is nested
  if (Array.isArray((node as any).items)) {
    const keyParts = ((node as any).key || "").split(".");
    const fieldKeyName = keyParts[keyParts.length - 1]; // Get last part

    if (canAddItems(formType || null, fieldKeyName)) {
      const dynamicItem: ContentItem = {
        type: "dynamic_items",
        key: (node as any).key || "unknown",
        label: (node as any).label || fieldKeyName,
        items: (node as any).items,
        indentLevel,
      };
      return [dynamicItem];
    }
  }

  let items: ContentItem[] = [];
  const hasChildrenOrFields = childItems.length > 0 || fieldItems.length > 0;
  if (node.label && hasChildrenOrFields) {
    items.push(createLabelItem(node.label || "", node.key, indentLevel));
  }

  items.push(...fieldItems);
  items.push(...childItems);

  return items;
}

/**
 * Checks if a node represents a document.
 */
function checkIfDocumentNode(node: {
  label?: string;
  fields?: Record<string, FieldObject>;
}): boolean {
  return !!(
    node.fields?.documentName?.value ||
    node.fields?.presignedUrl?.value ||
    (node.label &&
      typeof node.label === "string" &&
      node.label.toLowerCase().includes("document"))
  );
}

/**
 * Creates a document item from a node.
 */
function createDocumentItem(
  node: {
    fields?: Record<string, FieldObject>;
    label?: string;
    key?: string;
  },
  indentLevel: number
): ContentItem {
  const docName = node.fields?.documentName?.value || node.label || "";
  const docType = node.fields?.documentType?.value || "";
  const url = node.fields?.presignedUrl?.value || "";

  return {
    type: "document",
    key: node.key ?? String(docName),
    label: String(docName),
    fileType: String(docType),
    url: String(url),
    indentLevel,
  };
}

/**
 * Creates a label item.
 */
function createLabelItem(
  label: string,
  key: string | undefined,
  indentLevel: number
): ContentItem {
  const level: "top" | "sub" | "subsub" =
    indentLevel === 0 ? "top" : indentLevel === 1 ? "sub" : "subsub";

  return {
    type: "label",
    text: label,
    indentLevel,
    key: key ?? label,
    level,
  };
}

/**
 * Processes fields in a node.
 */
function processFields(
  fields: Record<string, FieldObject | any>,
  parentKey: string | undefined,
  indentLevel: number,
  formType?: string | null
): ContentItem[] {
  const items: ContentItem[] = [];

  Object.entries(fields).forEach(([fieldKey, fieldObj]) => {
    const fullKey = fieldObj.key ?? `${parentKey}.${fieldKey}`;

    // Handle field with nested items
    if (Array.isArray(fieldObj.items)) {
    }

    // Handle field with nested fields
    if (
      fieldObj &&
      typeof fieldObj === "object" &&
      ("fields" in fieldObj || "items" in fieldObj)
    ) {
      items.push(
        ...flattenContent(
          {
            ...fieldObj,
            key: fullKey,
            label: fieldObj.label ?? fieldKey,
          },
          indentLevel,
          formType
        )
      );
      return;
    }

    // Normal field
    items.push({
      type: "field",
      key: fullKey,
      label: fieldObj.label,
      value: fieldObj.value,
      reviewMode: fieldObj.reviewMode,
      indentLevel: indentLevel + 1,
    });
  });

  return items;
}

/**
 * Processes child nodes in a node.
 */
function processChildNodes(
  node: Record<string, unknown>,
  indentLevel: number,
  formType?: string | null
): ContentItem[] {
  return Object.entries(node)
    .filter(([key]) => !["label", "key", "fields"].includes(key))
    .flatMap(([, childValue]) => {
      if (childValue && typeof childValue === "object") {
        return flattenContent(
          childValue as {
            label?: string;
            key?: string;
            fields?: Record<string, FieldObject>;
          },
          indentLevel + 1,
          formType
        );
      }
      return [];
    });
}

/**
 * Splits items into pages of MAX_ITEMS_PER_PAGE each.
 * Simplified version without caching - useMemo handles memoization at the component level.
 */
export function chunkItems(items: ContentItem[]): ContentItem[][] {
  const pages: ContentItem[][] = [];
  for (let i = 0; i < items.length; i += MAX_ITEMS_PER_PAGE) {
    pages.push(items.slice(i, i + MAX_ITEMS_PER_PAGE));
  }
  return pages;
}

export const formatPhoneNumber = (
  value: string | number | null | undefined
): string => {
  if (value === null || value === undefined) return "";
  const strValue = typeof value === "string" ? value : String(value);
  const digits = strValue.replace(/\D/g, "").slice(0, 10);
  const len = digits.length;

  if (len === 0) return "";

  if (len <= 3) return `(${digits}`;
  if (len <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;

  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const defaultIsSensitiveField = (keyName: string): boolean => {
  const lower = keyName.toLowerCase();
  return lower === "ssn";
};

export const formatSSNNumber = (
  value: string | number | null | undefined
): string => {
  if (value === null || value === undefined) return "";
  const strValue = typeof value === "string" ? value : String(value);
  const digits = strValue.replace(/\D/g, "").slice(0, 9);
  const len = digits.length;

  if (len <= 3) return digits;
  if (len <= 5) return `${digits.slice(0, 3)}-${digits.slice(3)}`;

  return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
};

export const formatEmail = (value: string): string => {
  return value.trim().toLowerCase().replace(/\s+/g, "");
};

export const delayMS = (ms: number) =>
  new Promise<void>((r) => setTimeout(r, ms));

/**
 * Navigates to the field with given key in review mode.
 * @param fieldKey The key of the field to navigate to.
 * @param blockType One of `center` or `nearest`
 */
export const navigateToField = async (
  fieldKey: string,
  blockType: ScrollLogicalPosition = "center"
) => {
  const element = document.getElementById(`review-item-${fieldKey}`);
  if (element) {
    element.scrollIntoView({ behavior: "smooth", block: blockType });
  } else {
    showNotification({
      color: "red",
      message: "Could not find field with diagnostic error :(",
    });
    console.error(`Could not find review item: ${fieldKey}`);
  }
};

export const findDocumentById = (documentId: string): DocumentItem | null => {
  const { documents } = useProfileStore.getState();
  // documents is now a flat array after refactoring
  for (const document of documents) {
    if (document.docId == documentId) {
      return document;
    }
  }
  return null;
};
